<script setup lang="ts">
import { computed, ref } from 'vue';

import type {
  IAffiliateChangePaymentSystemData,
  ICurrentPaymentInfo,
  PaymentSystemName,
} from '@modules/payment-system-details/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useI18n } from '@modules/i18n/composables';
import { usePaymentSystemForm } from '@modules/payment-system-details/composables';
import { PAYMENT_SYSTEM_DETAILS_PERMISSIONS, TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE } from '@modules/payment-system-details/constants';
import { useCancelPaymentSystemMutation, useChangePaymentSystemMutation } from '@modules/payment-system-details/mutations';
import { useShowCurrentPaymentInfoQuery } from '@modules/payment-system-details/queries';
import {
  EPaymentSystemStatus,
} from '@modules/payment-system-details/types';
import { useUserSupervision } from '@modules/user/composables';

import AffiliatePaymentSystemDialog from '../AffiliatePaymentSystemDialog/AffiliatePaymentSystemDialog.vue';
import VAffiliatePaymentSystem from '../VAffiliatePaymentSystem/VAffiliatePaymentSystem.vue';
import PaymentSystemRadioItem from '../VPaymentSystemRadioItem/VPaymentSystemRadioItem.vue';

const paymentSystemDialog = ref(false);
const viewPaymentSystemDetails = ref(false);
const paymentSystemName = ref<PaymentSystemName>();

const { getTranslation } = useI18n();
const { confirm } = useConfirmDialogs();
const { isSupervision } = useUserSupervision();

const { data: showPaymentSystemData, isLoading: isCurrentPaymentInfoLoading } = useShowCurrentPaymentInfoQuery();

const {
  mutateAsync: savePaymentSystem,
  isPending: isSaveLoading,
  errors,
  clearError,
} = useChangePaymentSystemMutation();

const {
  mutateAsync: cancelPaymentSystem,
  isPending: isCancelLoading,
} = useCancelPaymentSystemMutation();

const currentPaymentSystem = computed<ICurrentPaymentInfo | undefined>(() => showPaymentSystemData.value?.payment_system);
const currentPaymentSystemName = computed<PaymentSystemName | undefined>(() => currentPaymentSystem.value?.name);

const changedPaymentSystem = computed<IAffiliateChangePaymentSystemData | undefined>(() => showPaymentSystemData.value?.payment_change_form);
const changedPaymentSystemName = computed<PaymentSystemName | undefined>(() => changedPaymentSystem.value?.new_payment_method?.name);

const {
  paymentSettingsForm,
  currentFormValues,
  paymentSystemSchema,
  paymentSystemsNamesList,
  isDataLoading,
  changeCurrentFormValues,
  resetFormValues,
  onFormChange,
  onSubmit,
} = usePaymentSystemForm(paymentSystemName);

const isLoading = computed(() => isDataLoading.value || isCurrentPaymentInfoLoading.value);

async function onSavePaymentSystem(): Promise<void> {
  if (!currentFormValues.value)
    return;

  try {
    await savePaymentSystem({
      id: changedPaymentSystem.value?.id,
      payment_system: currentFormValues.value,
    });
    onPaymentSystemSettingsHide();
  } catch {}
}

async function onCancelPaymentSystem(): Promise<void> {
  confirm({
    group: 'confirmPopup',
    header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'CONFIRM_CANCELING_HEADER').value,
    message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'CONFIRM_CANCELING_MESSAGE').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'CONFIRM_CANCELING_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'CONFIRM_CANCELING_NO').value,
    accept: () => {
      if (changedPaymentSystem.value?.id) {
        void cancelPaymentSystem(changedPaymentSystem.value?.id);
      }
    },
  });
}

function onPaymentSystemEdit(name: PaymentSystemName, paymentSystemInfo?: ICurrentPaymentInfo): void {
  paymentSystemName.value = name;
  changeCurrentFormValues(paymentSystemInfo);
  paymentSystemDialog.value = true;
}

function onPaymentSystemShow(name: PaymentSystemName, paymentSystemInfo?: ICurrentPaymentInfo): void {
  onPaymentSystemEdit(name, paymentSystemInfo);
  viewPaymentSystemDetails.value = true;
}

function onPaymentSystemSettingsOpen(): void {
  paymentSystemDialog.value = true;

  if (!paymentSystemName.value) {
    if (currentPaymentSystemName.value) {
      paymentSystemName.value = currentPaymentSystemName.value;
      changeCurrentFormValues(currentPaymentSystem.value);
    } else if (paymentSystemsNamesList.value?.length) {
      paymentSystemName.value = paymentSystemsNamesList.value[0];
    }
  }
}

function onPaymentSystemSettingsHide(): void {
  paymentSystemDialog.value = false;
  resetFormValues();
  paymentSystemName.value = undefined;
  viewPaymentSystemDetails.value = false;
}
</script>

<template>
  <div v-data-qa-auto-id="'AffiliatePaymentSystemSettings'">
    <div>
      <VLoader
        :loading="isLoading"
        min-height="87px"
      >
        <div class="mb-6 flex flex-col items-start">
          <div>
            <VAffiliatePaymentSystem
              :translate-key="TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE"
              :payment-system-name="currentPaymentSystemName"
              :payment-system-sub-field="currentPaymentSystem?.payment_method_short"
              :status="EPaymentSystemStatus.APPROVED"
              @show="onPaymentSystemShow($event, currentPaymentSystem)"
            />
          </div>
          <div v-if="changedPaymentSystem">
            <PDivider class="mx-0 my-2" />
            <VAffiliatePaymentSystem
              :translate-key="TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE"
              :payment-system-name="changedPaymentSystemName"
              :payment-system-sub-field="changedPaymentSystem?.new_payment_method.payment_method_short"
              :contacts="changedPaymentSystem?.manager_contacts"
              :status="changedPaymentSystem?.status"
              :reject-reason="changedPaymentSystem?.rejection_reason"
              :is-loading="isSaveLoading || isCancelLoading"
              :disabled-cancel-btn="isSupervision"
              @show="onPaymentSystemShow($event, changedPaymentSystem?.new_payment_method)"
              @edit="onPaymentSystemEdit($event, changedPaymentSystem?.new_payment_method)"
              @cancel="onCancelPaymentSystem"
            />
          </div>
        </div>
      </VLoader>

      <VButton
        v-if="$hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.CREATE)"
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'CONFIGURE_PAYMENT_METHOD_BUTTON')"
        :disabled="isSupervision || !!changedPaymentSystem || isLoading"
        @click="onPaymentSystemSettingsOpen"
      />
    </div>
    <AffiliatePaymentSystemDialog
      v-model:visible="paymentSystemDialog"
      :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'SETTINGS_MODAL_HEADER')"
      :left-column-title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'SETTINGS_MODAL_LEFT_COLUMN_TITLE')"
      :right-column-title="viewPaymentSystemDetails
        ? undefined
        : $getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'SETTINGS_MODAL_RIGHT_COLUMN_TITLE')
      "
      :size="viewPaymentSystemDetails ? 'small' : 'large'"
      sticky-sides
      sticky-footer
      @hide="onPaymentSystemSettingsHide"
    >
      <template
        v-if="!viewPaymentSystemDetails"
        #leftSide
      >
        <div class="flex flex-col gap-4 input-max-width">
          <PaymentSystemRadioItem
            v-for="systemName in paymentSystemsNamesList"
            :key="systemName"
            v-model="paymentSystemName"
            :disabled="isSupervision"
            name="payment-system"
            :value="systemName"
            :label="systemName"
          />
        </div>
      </template>
      <template #rightSide>
        <VForm
          ref="paymentSettingsForm"
          :schema="paymentSystemSchema"
          :readonly="viewPaymentSystemDetails || isSupervision"
          :is-loading="isSaveLoading"
          :custom-errors="errors"
          @interacted="clearError"
          @change="onFormChange"
          @submit="onSavePaymentSystem"
        />
      </template>
      <template
        v-if="!viewPaymentSystemDetails"
        #footer
      >
        <div class="flex justify-end gap-2">
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'SETTINGS_MODAL_CLOSE')"
            outlined
            @click="onPaymentSystemSettingsHide"
          />
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, 'SETTINGS_MODAL_SAVE')"
            :loading="isSaveLoading"
            :disabled="isSupervision"
            @click="onSubmit"
          />
        </div>
      </template>
    </AffiliatePaymentSystemDialog>
  </div>
</template>
